import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Chip
} from '@mui/material';
import { getUserProfile, updateUserProfile, syncXboxLibrary, updateStatusCounts, checkXboxSyncStatus } from '../../services/api';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import AccessTimeIcon       from '@mui/icons-material/AccessTime';
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineIcon     from '@mui/icons-material/ErrorOutline';
import InfoIcon from '@mui/icons-material/Info';
import Dialog from '../dialogs/Dialog';
import api from '../../services/api';

const SyncStatusDisplay: React.FC<{
  status: number | null;
  message: string | null;
  lastSyncTime: string | null;
  nextSyncTime: string | null;
}> = ({ status, message, lastSyncTime, nextSyncTime }) => {
  if (status === null) return null;

  let icon: React.ReactNode;
  let label: string;
  let timeLabel: string | null = null;
  
  switch (status) {
    case 0:
      icon = <HourglassEmptyIcon color="disabled" />;
      label = '待機中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 1:
      icon = <AccessTimeIcon color="info" />;
      label = 'キュー待ち';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 2:
      icon = <CircularProgress size={20} />;
      label = '処理中';
      timeLabel = lastSyncTime ? `前回の同期: ${lastSyncTime} JST` : null;
      break;
    case 3:
      icon = <CheckCircleOutlineOutlinedIcon color="success" />;
      label = '完了';
      timeLabel = lastSyncTime ? `同期完了: ${lastSyncTime} JST` : null;
      break;
    case 4:
      icon = <ErrorOutlineIcon color="error" />;
      label = '失敗';
      // エラー時は30分後に再試行可能なことを表示
      if (lastSyncTime) {
        const syncDate = new Date(lastSyncTime.replace(' JST', ''));
        const retryDate = new Date(syncDate.getTime() + 1 * 9000); // 9秒後
        const now = new Date();
        
        if (now >= retryDate) {
          timeLabel = '再試行可能です';
        } else {
          const seconds = Math.ceil((retryDate.getTime() - now.getTime()) / 1000);
          timeLabel = `再試行まで約${seconds}秒`;
        }
      }
      break;
    default:
      icon = null;
      label = '';
  }

  return (
    <Paper 
      variant="outlined" 
      sx={{ 
        p: 2, 
        mb: 2,
        bgcolor: status === 4 ? 'rgba(211, 47, 47, 0.04)' : 'inherit' // エラー時は薄い赤背景
      }}
    >
      <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
        <Box display="flex" alignItems="center" gap={1}>
          {icon}
          <Typography variant="body2" fontWeight="500" whiteSpace="nowrap">
            {label}
          </Typography>
        </Box>
        {message && (
          <Typography variant="caption" color="text.secondary">
            {message}
          </Typography>
        )}
      </Box>
      {timeLabel && (
        <Box mt={1}>
          <Typography variant="caption" color="text.secondary">
            {timeLabel}
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

const XboxSettings: React.FC = () => {
  const [xboxId, setXboxId] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);
  const [syncLoading, setSyncLoading] = useState<boolean>(false);
  const [success, setSuccess] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [hasXboxId, setHasXboxId] = useState<boolean>(false);
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [lastUpdateTime, setLastUpdateTime] = useState<number>(0);
  const [nextSyncTime, setNextSyncTime] = useState<string | null>(null);
  const [lastSyncTime, setLastSyncTime] = useState<string | null>(null);
  const [canSync, setCanSync] = useState<boolean>(true);
  const [syncStatus, setSyncStatus] = useState<number | null>(null);
  const [syncStatusMessage, setSyncStatusMessage] = useState<string | null>(null);
  const [showPrivacyDialog, setShowPrivacyDialog] = useState<boolean>(false);
  const [showDevelopmentInfoDialog, setShowDevelopmentInfoDialog] = useState<boolean>(false);
  const pollingRef = useRef<NodeJS.Timeout | null>(null);

  // 開発環境かどうかを判定
  const isDevelopment = process.env.NODE_ENV === 'development';

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        setLoading(true);
        const response = await getUserProfile();
        if (response.data.xbox_id) {
          setXboxId(response.data.xbox_id);
          setHasXboxId(true);
          // プロフィール取得成功後に同期ステータスをチェック
          checkSyncStatus();
        }
      } catch (err) {
        setError('プロフィール情報の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
    
    // コンポーネントのアンマウント時にポーリングをクリア
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // 同期ステータスを確認する関数
  const checkSyncStatus = async () => {
    try {
      const result = await checkXboxSyncStatus();
      setSyncStatus(result.status);
      setSyncStatusMessage(result.message);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      if (result.last_sync_at) {
        const dt = new Date(result.last_sync_at);
        const formattedLast = dt.toLocaleString('ja-JP', {
          year: 'numeric', month: '2-digit', day: '2-digit',
          hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false
        }).replace(/\//g, '-').replace(/,/g, '');
        setLastSyncTime(formattedLast);
      }
      
      // ステータスに基づいて同期ボタンの状態を更新
      setCanSync(result.can_sync && result.status !== 2); // 処理中でなく、同期可能であれば
      
      // 処理中(2)の場合、定期的にステータスを確認
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000); // 10秒ごとに確認
      } 
      // 処理中でなくなったらポーリングを停止
      else if (result.status !== 2 && pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
      
    } catch (err) {
      console.error('同期ステータスの確認に失敗:', err);
    }
  };

  const handleXboxAuth = () => {
    const redirectUri = encodeURIComponent(`${window.location.origin}/auth/xbox/callback`);
    const xboxAuthUrl = `https://login.microsoftonline.com/${process.env.REACT_APP_XBOX_TENANT_ID || 'common'}/oauth2/v2.0/authorize?client_id=${process.env.REACT_APP_XBOX_CLIENT_ID}&response_type=code&redirect_uri=${redirectUri}&scope=openid%20profile%20User.Read&response_mode=query`;
    
    window.location.href = xboxAuthUrl;
  };

  const handleSyncLibrary = async () => {
    setShowPrivacyDialog(true);
  };

  const handleConfirmSync = async () => {
    setShowPrivacyDialog(false);
    try {
      setSyncLoading(true);
      setError(null);
      setSuccess(null);
      
      const result = await syncXboxLibrary();
      setSuccess(result.message || 'Xboxライブラリの同期を開始しました');
      
      // エラー状態の場合は同期可能フラグを true に設定
      setCanSync(result.status === 4);
      setSyncStatus(result.status);
      
      if (result.next_sync_time) {
        setNextSyncTime(result.next_sync_time);
      }
      
      // ポーリングを開始
      if (result.status === 2 && !pollingRef.current) {
        pollingRef.current = setInterval(checkSyncStatus, 10000);
      }
    } catch (err: any) {
      if (err.response?.status === 429) {
        setNextSyncTime(err.response.data.next_sync_time);
        setError(err.response.data.message);
      } else {
        setError(err.message || 'Xboxライブラリの同期に失敗しました');
        // エラー時は同期可能フラグを true に設定
        setCanSync(true);
      }
    } finally {
      setSyncLoading(false);
    }
  };

  const handleUpdateStatusCounts = async () => {
    const now = Date.now();
    if (now - lastUpdateTime < 2000) {
      return;
    }

    try {
      setUpdateLoading(true);
      setError(null);
      setSuccess(null);
      setLastUpdateTime(now);
      
      const [result] = await Promise.all([
        updateStatusCounts(),
        new Promise(resolve => setTimeout(resolve, 2000))
      ]);
      
      setSuccess('ステータスカウントが更新されました');
    } catch (err: any) {
      setError(err.message || 'ステータスカウントの更新に失敗しました');
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUnlinkXbox = async () => {
    if (!window.confirm('Xboxアカウントの連携を解除してもよろしいですか？')) {
      return;
    }

    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      await api.delete('/profile/xbox/unlink/');
      setXboxId('');
      setHasXboxId(false);
      setSuccess('Xboxアカウントの連携を解除しました');
      
      // ポーリングを停止
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
        pollingRef.current = null;
      }
    } catch (err: any) {
      setError(err.response?.data?.error || 'Xboxアカウントの連携解除に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  const privacyDialogContent = (
    <Box>
      <Typography variant="body1" gutterBottom>
        Xbox同期機能は現在ベータ版として提供されています。
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mt: 2, mb: 2 }}>
        現在、Microsoft Graph APIを使用したXboxライブラリの詳細な同期機能は開発中です。
        将来的にはXbox Game Passライブラリとの本格的な同期が可能になる予定です。
      </Typography>
      <Alert 
        severity="info" 
        sx={{ 
          mb: 2,
          '& .MuiAlert-message': {
            fontSize: '0.8rem',
            lineHeight: '1'
          }
        }}
      >
        <Typography variant="caption">
          Xbox Syncは現在ベータ版です。
          今後のアップデートでは、Xbox Game Pass APIを使用した
          ライブラリの詳細な同期機能を実装予定です。
        </Typography>
      </Alert>
      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => setShowPrivacyDialog(false)} color="inherit" size="small">
          キャンセル
        </Button>
        <Button onClick={handleConfirmSync} variant="contained" color="primary" size="small">
          同期を開始
        </Button>
      </Box>
    </Box>
  );

  const developmentInfoDialogContent = (
    <Box>
      <Typography variant="body2" paragraph>
        Xbox同期機能は開発停止中です。いくつかの課題により、実装を見送っております。
      </Typography>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
          APIの現状
        </Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          • Microsoft公式APIではXboxのゲームライブラリ詳細情報の取得ができない<br/>
          • 非公式APIは有料での提供が基本となっており、継続的なコストが発生<br/>
        </Typography>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
          その他の考慮
        </Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          • 日本国内でのXbox普及率を考慮して開発優先度を設定<br/>
          • Steam等の他プラットフォーム連携の強化を優先<br/>
        </Typography>
      </Box>
      
      <Box sx={{ mb: 2 }}>
        <Typography variant="subtitle2" gutterBottom sx={{ color: 'primary.main' }}>
          今後の予定
        </Typography>
        <Typography variant="body2" paragraph color="text.secondary">
          • PULSEの開発コスト回収もしくは運用コストの安定化後に再検討予定<br/>
        </Typography>
      </Box>
      
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="caption">
          最新の開発状況については公式SNSアカウントでお知らせいたします。ご理解のほどよろしくお願いいたします。
        </Typography>
      </Alert>
      
      <Box sx={{ mt: 3, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
        <Button onClick={() => setShowDevelopmentInfoDialog(false)} variant="contained" size="small">
          閉じる
        </Button>
      </Box>
    </Box>
  );

  if (loading && !hasXboxId) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={200}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Xbox / Microsoft 連携設定
      </Typography>

      <Paper elevation={0} sx={{ mb: 3, bgcolor: 'background.paper' }}>

        <Typography variant="body2" paragraph color="text.secondary">
          アカウントを連携することで、PULSEへのログインにXbox/Microsoft認証を利用できます。認証方法の紛失など万が一の際にもアカウントを保護することができます。将来的にはXboxでのゲームデータを同期できるようになります。
        </Typography>

        <Box sx={{ mb: 3 }}>
          {hasXboxId ? (
            <>
              <Alert
                severity="success"
                sx={{
                  mb: 2,
                  maxWidth: 500,
                  background: 'linear-gradient(135deg, rgba(107, 185, 107, 0.2), rgba(85, 139, 85, 0.2))',
                  color: '#ffffff',
                  border: '1px solid rgba(107, 185, 107, 0.2)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(8px)',
                  '& .MuiAlert-icon': {
                    color: '#6bb96b',
                  },
                }}
              >
                Xboxアカウントが連携されています。Xbox ID: {xboxId}
              </Alert>
              <Box sx={{ display: 'flex', gap: 0, justifyContent: 'flex-end', mt: 0 }}>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={handleXboxAuth}
                  disabled={loading}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'text.secondary',
                    cursor: loading ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.7rem',
                    opacity: loading ? 0.5 : 1,
                    '&:hover': {
                      color: loading ? 'text.secondary' : 'primary.main',
                    },
                  }}
                >
                  再連携
                </Typography>
                <Typography
                  component="button"
                  variant="caption"
                  onClick={handleUnlinkXbox}
                  disabled={loading}
                  sx={{
                    background: 'none',
                    border: 'none',
                    color: 'text.secondary',
                    cursor: loading ? 'default' : 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.7rem',
                    opacity: loading ? 0.5 : 1,
                    '&:hover': {
                      color: loading ? 'text.secondary' : 'error.main',
                    },
                  }}
                >
                  連携を解除
                </Typography>
              </Box>
            </>
          ) : (
            <>
              <Button
                variant="contained"
                onClick={handleXboxAuth}
                disabled={loading}
                size="small"
                startIcon={
                  <img 
                    src="/icons/service/xbox.svg" 
                    alt="Xbox" 
                    style={{ width: '18px', height: '18px', filter: 'invert(1)' }}
                  />
                }
                sx={{ 
                  mb: 2,
                  backgroundColor: '#107C10',
                  color: '#ffffff',
                  '&:hover': {
                    backgroundColor: '#0E6A0E',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(16, 124, 16, 0.3)',
                  }
                }}
              >
                Xboxで認証する
              </Button>
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Box display="flex" alignItems="center">
            <Typography variant="subtitle1">
              Xbox Sync 
            </Typography>
            <Chip 
              label={isDevelopment ? "DEV v0.1.0" : "保留中"} 
              size="small" 
              color={isDevelopment ? "default" : "warning"} 
              variant="outlined" 
              sx={{ 
                ml: 1, 
                height: 18, 
                borderColor: isDevelopment ? 'rgba(107, 185, 107, 0.5)' : 'rgba(255, 152, 0, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '4px',
                fontSize: '11px',
                color: isDevelopment ? 'rgba(107, 185, 107, 1)' : 'rgba(255, 152, 0, 1)',
              }} 
            />
          </Box>
          {isDevelopment ? (
            <>
              <Typography variant="body2" paragraph color="text.secondary">
                XboxのゲームをPULSEライブラリと同期します。
                Xbox Game Pass APIの実装は現在開発中のため、基本的な同期機能のみ提供されています。
              </Typography>

              <SyncStatusDisplay
                status={syncStatus}
                message={syncStatusMessage}
                lastSyncTime={lastSyncTime}
                nextSyncTime={nextSyncTime}
              />

              {(success && success.includes('同期')) && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  {success}
                </Alert>
              )}

              {(error && error.includes('同期')) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {error}
                </Alert>
              )}

              <Button
                variant="outlined"
                color="primary"
                onClick={handleSyncLibrary}
                disabled={syncLoading || !hasXboxId || !canSync}
                startIcon={syncLoading ? <CircularProgress size={20} /> : null}
                sx={{ mt: 1 }}
                size="small"
              >
                {canSync ? 'ライブラリを同期' : syncStatus === 2 ? '同期中...' : 'ライブラリを同期'}
              </Button>
            </>
          ) : (
            <>
              <Typography variant="body2" paragraph color="text.secondary">
                Xboxライブラリ同期機能は準備中です。
              </Typography>
              
              {/* <Alert 
                severity="warning" 
                sx={{ 
                  mt: 1, 
                  mb: 2,
                  '& .MuiAlert-message': {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%'
                  }
                }}
              >
                <Box>
                  <Typography variant="body2">
                    より詳しい状況については詳細ダイアログからご確認ください。
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<InfoIcon />}
                  onClick={() => setShowDevelopmentInfoDialog(true)}
                  sx={{
                    ml: 2,
                    flexShrink: 0,
                    borderColor: 'warning.main',
                    color: 'warning.main',
                    '&:hover': {
                      borderColor: 'warning.dark',
                      backgroundColor: 'rgba(255, 152, 0, 0.04)'
                    }
                  }}
                >
                  詳細
                </Button>
              </Alert> */}
            </>
          )}
        </Box>

        <Divider sx={{ my: 3 }} />

        <Box>
          <Typography variant="subtitle1" gutterBottom>
            ステータスカウント修復ツール
          </Typography>

          <Typography variant="body2" paragraph color="text.secondary">
            ライブラリのステータスカウントが正しく集計されていない場合、手動で修復できます。
          </Typography>

          {(success && !success.includes('同期')) && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {success}
            </Alert>
          )}

          {(error && !error.includes('同期')) && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Button
            variant="outlined"
            color="secondary"
            onClick={handleUpdateStatusCounts}
            disabled={updateLoading || Date.now() - lastUpdateTime < 2000}
            startIcon={updateLoading ? <CircularProgress size={20} /> : null}
            sx={{
              mt: 1,
              color: "text.secondary",
              borderColor: "divider",
              "&:hover": {
                backgroundColor: "action.hover",
                borderColor: "text.secondary",
              },
            }}
            size="small"
          >
            {updateLoading ? "更新中..." : "ライブラリを修復"}
          </Button>
        </Box>

        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </Paper>

      <Dialog
        open={showPrivacyDialog}
        onClose={() => setShowPrivacyDialog(false)}
        title="Xbox同期について"
        content={privacyDialogContent}
        showCloseButton={false}
        width={480}
      />

      <Dialog
        open={showDevelopmentInfoDialog}
        onClose={() => setShowDevelopmentInfoDialog(false)}
        title="Xbox Sync 開発状況"
        content={developmentInfoDialogContent}
        showCloseButton={false}
        width={520}
      />
    </Box>
  );
};

export default XboxSettings; 